import React from "react"
import { Div, Text } from "atomize"
import { motion, useReducedMotion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { theme } from "../settings/config"

// I pezzi dei fogli su chi c'è nel circolo — la scheda Maestri, la scheda
// della persona, il codice d'ingresso — che devono sembrare la stessa cosa:
// blocchi bianchi sul fondo caldo, righe che si toccano, scelte una sotto
// l'altra, e bottoni larghi per le azioni che cambiano chi c'è.

// I ruoli già pronti, ognuno con la sua frase
export const ROLE_HINTS = {
	colleague: "Le sue lezioni, e quelle che gli affidi",
	viewer: "Vede giornata e riepilogo di tutti i maestri",
	manager:
		"Modifica e assegna le lezioni di tutti, fa entrare colleghi, cambia campi, listino e dati",
}

// Un blocco bianco con gli angoli a 15px: sul fondo caldo del foglio tiene
// insieme le righe che parlano della stessa cosa.
export function Block({ children, ...props }) {
	return (
		<Div bg={theme.colors.surface} rounded="15px" p={{ x: "0.875rem" }} {...props}>
			{children}
		</Div>
	)
}

// La riga sottile tra due righe di un blocco: parte dal testo, non dal bordo,
// così il tondo a sinistra resta libero.
export const Divider = ({ inset = "3.25rem" }) => (
	<Div h="1px" bg={theme.colors.gray_light} m={{ l: inset }} />
)

// Una riga che si tocca: un bottone vero, così ci si arriva anche da
// tastiera.
export function RowButton({ children, onClick, label, style, ...rest }) {
	return (
		<motion.button
			type="button"
			aria-label={label}
			whileTap={{ scale: 0.99 }}
			onClick={onClick}
			{...rest}
			style={{
				display: "flex",
				alignItems: "center",
				gap: "0.75rem",
				width: "100%",
				padding: "0.75rem 0",
				border: 0,
				background: "transparent",
				textAlign: "left",
				cursor: "pointer",
				font: "inherit",
				color: "inherit",
				...style,
			}}>
			{children}
		</motion.button>
	)
}

// Una scelta tra poche, col tondo a sinistra: pieno d'arancione quella
// scelta. Il titolo dice cosa, la riga sotto cosa vuol dire.
export function RadioRow({ title, hint, isSelected, onSelect }) {
	return (
		<RowButton
			role="radio"
			aria-checked={isSelected}
			onClick={onSelect}
			style={{ alignItems: "flex-start" }}>
			<Div
				aria-hidden="true"
				w="20px"
				h="20px"
				m={{ t: "0.15rem" }}
				rounded="circle"
				border={isSelected ? "6px solid" : "2px solid"}
				borderColor={isSelected ? theme.colors.primary : theme.colors.gunmetal_light}
				style={{ flexShrink: 0, boxSizing: "border-box" }}
			/>
			<Div>
				<Text textSize="body" textWeight={isSelected ? "bold" : "normal"}>
					{title}
				</Text>
				<Text textSize="caption" textColor={theme.colors.text_light}>
					{hint}
				</Text>
			</Div>
		</RowButton>
	)
}

// Le scelte di un blocco, divise da una riga sottile
export function RadioBlock({ options, selected, onSelect, ...props }) {
	return (
		<Block role="radiogroup" {...props}>
			{options.map((option, i) => (
				<React.Fragment key={option.value}>
					{i > 0 && <Divider inset="2rem" />}
					<RadioRow
						title={option.title}
						hint={option.hint}
						isSelected={selected === option.value}
						onSelect={() => onSelect(option.value)}
					/>
				</React.Fragment>
			))}
		</Block>
	)
}

// Le azioni che cambiano chi c'è — far entrare e togliere — sono bottoni
// pieni e larghi, alti come gli altri bottoni dell'app: non si devono
// cercare.
export function WideButton({ icon, text, bg, color, shadow, onClick }) {
	return (
		<motion.button
			type="button"
			whileTap={{ scale: 0.97 }}
			onClick={onClick}
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				gap: "0.6rem",
				width: "100%",
				height: "3.5rem",
				border: 0,
				borderRadius: "20px",
				background: bg,
				color,
				boxShadow: shadow ?? "none",
				font: "inherit",
				fontWeight: 600,
				cursor: "pointer",
			}}>
			{icon && <FontAwesomeIcon icon={icon} />}
			{text}
		</motion.button>
	)
}

// Quello che scorre, dentro un foglio: come negli altri fogli dell'app, il
// contenuto sta in un'area alta al più tre quinti dello schermo, e i bottoni
// restano fermi sotto. Senza, un foglio che si allunga — un "?" aperto per
// intero, un circolo con tanti maestri — usciva dallo schermo, e la cima
// finiva sotto la fascia arancione.
export function ScrollArea({ children }) {
	return (
		<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
			{children}
		</Div>
	)
}

// Il passaggio da una fase all'altra di un foglio — dall'elenco alla scheda
// di una persona, da una domanda alla successiva — scivola di lato: avanti
// entra da destra, indietro da sinistra, come una pagina che si gira. Al
// primo giro non si muove niente: sale già il foglio. Chi ha chiesto al
// telefono meno movimento la vede comparire e basta.
export function StepTransition({ stepKey, direction = 0, children }) {
	const reduceMotion = useReducedMotion()
	const moves = direction !== 0 && !reduceMotion

	return (
		<motion.div
			key={stepKey}
			initial={moves ? { x: 32 * direction, opacity: 0 } : false}
			animate={{ x: 0, opacity: 1 }}
			transition={{ type: "tween", ease: [0.04, 0.62, 0.23, 0.98], duration: 0.3 }}>
			{children}
		</motion.div>
	)
}
